import type { OfflineAction } from "./actions";
import { type OutboxDatabase, openOutboxDb, readAllItems, removeItem, writeItem } from "./db";
import { staleView } from "./legacyItem";
import type { OutboxFailure, OutboxItem, OutboxSnapshot, StoredOutboxItem } from "./types";

export interface EnqueueOptions {
  occurredAt?: Date;
}

const EMPTY_SNAPSHOT: OutboxSnapshot = {
  items: [],
  stale: [],
  pending: [],
  failed: [],
  pendingCount: 0,
  failedCount: 0,
};

export function emptySnapshot(): OutboxSnapshot {
  return EMPTY_SNAPSHOT;
}

function buildSnapshot(items: OutboxItem[], stale: StoredOutboxItem[]): OutboxSnapshot {
  const pending = items.filter((item) => item.status === "pending");
  const failed = items.filter((item) => item.status === "failed");
  return {
    items,
    stale: stale.map(staleView),
    pending,
    failed,
    pendingCount: pending.length,
    failedCount: failed.length,
  };
}

let sequence = 0;

function newItemId(createdAt: number): string {
  sequence += 1;
  const stamp = createdAt.toString().padStart(14, "0");
  const order = sequence.toString().padStart(8, "0");
  return `${stamp}-${order}-${crypto.randomUUID()}`;
}

export class UnclaimedOutboxError extends Error {}

export class OutboxStore {
  private readonly db: OutboxDatabase;

  private ownerKey: string | null = null;

  private items: OutboxItem[];

  private stale: StoredOutboxItem[];

  private snapshot: OutboxSnapshot;

  private readonly listeners = new Set<() => void>();

  private constructor(db: OutboxDatabase, items: OutboxItem[], stale: StoredOutboxItem[]) {
    this.db = db;
    this.items = items;
    this.stale = stale;
    this.snapshot = buildSnapshot(this.own(this.items), this.own(this.stale));
  }

  static async open(): Promise<OutboxStore> {
    const db = await openOutboxDb();
    const items: OutboxItem[] = [];
    const stale: StoredOutboxItem[] = [];
    for (const stored of await readAllItems(db)) {
      if (isFieldLeadItem(stored)) {
        items.push(stored);
      } else {
        stale.push(stored);
      }
    }
    return new OutboxStore(db, sortByDispatchOrder(items), sortByDispatchOrder(stale));
  }

  close(): void {
    this.db.close();
  }

  getSnapshot(): OutboxSnapshot {
    return this.snapshot;
  }

  currentOwnerKey(): string | null {
    return this.ownerKey;
  }

  release(): void {
    this.ownerKey = null;
    this.publish();
  }

  async claim(ownerKey: string): Promise<void> {
    this.ownerKey = ownerKey;
    this.items = await this.adopt(this.items, ownerKey);
    this.stale = await this.adopt(this.stale, ownerKey);
    this.publish();
  }

  private async adopt<T extends StoredOutboxItem>(items: T[], ownerKey: string): Promise<T[]> {
    const adopted: T[] = [];
    for (const item of items) {
      if ((item.ownerKey ?? null) === null) {
        const owned = { ...item, ownerKey };
        await writeItem(this.db, owned);
        adopted.push(owned);
      } else {
        adopted.push(item);
      }
    }
    return adopted;
  }

  private own<T extends StoredOutboxItem>(items: T[]): T[] {
    if (this.ownerKey === null) {
      return [];
    }
    return items.filter((item) => item.ownerKey === this.ownerKey);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async enqueue(action: OfflineAction, options: EnqueueOptions = {}): Promise<OutboxItem> {
    const ownerKey = this.ownerKey;
    if (ownerKey === null) {
      throw new UnclaimedOutboxError("La cola todavía no sabe de quién es esta sesión");
    }
    const occurredAt = options.occurredAt ?? new Date();
    const createdAt = new Date();
    const item: OutboxItem = {
      id: newItemId(createdAt.getTime()),
      ownerKey,
      action,
      occurredAt: occurredAt.toISOString(),
      status: "pending",
      attempts: 0,
      createdAt: createdAt.toISOString(),
      nextAttemptAt: createdAt.toISOString(),
      failure: null,
    };
    await writeItem(this.db, item);
    this.items = sortByDispatchOrder([...this.items, item]);
    this.publish();
    return item;
  }

  async remove(id: string): Promise<void> {
    await removeItem(this.db, id);
    this.items = this.items.filter((item) => item.id !== id);
    this.stale = this.stale.filter((item) => item.id !== id);
    this.publish();
  }

  async markFailed(id: string, failure: OutboxFailure): Promise<void> {
    await this.update(id, (item) => ({
      ...item,
      status: "failed",
      attempts: item.attempts + 1,
      failure,
    }));
  }

  async scheduleRetry(id: string, nextAttemptAt: Date, failure: OutboxFailure): Promise<void> {
    await this.update(id, (item) => ({
      ...item,
      status: "pending",
      attempts: item.attempts + 1,
      nextAttemptAt: nextAttemptAt.toISOString(),
      failure,
    }));
  }

  async retry(id: string, now: Date = new Date()): Promise<void> {
    await this.update(id, (item) => ({
      ...item,
      status: "pending",
      attempts: 0,
      nextAttemptAt: now.toISOString(),
      failure: null,
    }));
  }

  discard(id: string): Promise<void> {
    return this.remove(id);
  }

  private async update(id: string, change: (item: OutboxItem) => OutboxItem): Promise<void> {
    const current = this.items.find((item) => item.id === id);
    if (!current) {
      return;
    }
    const next = change(current);
    await writeItem(this.db, next);
    this.items = this.items.map((item) => (item.id === id ? next : item));
    this.publish();
  }

  private publish(): void {
    this.snapshot = buildSnapshot(this.own(this.items), this.own(this.stale));
    for (const listener of this.listeners) {
      listener();
    }
  }
}

function isFieldLeadItem(item: StoredOutboxItem): item is OutboxItem {
  return (
    item.action.kind === "field-lead.create" &&
    (item.status === "pending" || item.status === "failed")
  );
}

function sortByDispatchOrder<T extends StoredOutboxItem>(items: T[]): T[] {
  return [...items].sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
}
